import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import PublicNav from '@/components/PublicNav';
import Footer from '@/components/Footer';
import '@/styles/website.css';

/* ----------------------------- static data ----------------------------- */
const STEPS_BE = [
  { n: '01', t: 'Create your free profile', d: 'Add your portfolio, gear and location. Get quality-checked, then you’re in the network.' },
  { n: '02', t: 'Get pinged local jobs', d: 'New gigs in your area land in your feed the second they’re posted. Apply in a tap.' },
  { n: '03', t: 'Get booked & paid', d: 'Lock it in with a crew agreement, shoot, and get paid on time — no chasing invoices.' },
];
const STEPS_FIND = [
  { n: '01', t: 'Post your job in minutes', d: 'Set the role, date, location and your rate. No bidding wars, no lowballing.' },
  { n: '02', t: 'Reach vetted crew nearby', d: 'Your job pings instantly to quality-checked pros in the area. Watch the applications roll in.' },
  { n: '03', t: 'Hire with confidence', d: 'Pick your crew, lock it in with an agreement, and keep everyone on the same page.' },
];

const WHY_CARDS = [
  { icon: '📍', t: 'Local, the second it’s live', d: 'New jobs ping straight to crew nearby. Be first to know, first to apply.' },
  { icon: '🛡️', t: 'Vetted, every single member', d: 'Every profile is quality-checked with a valid ABN. You only work alongside real pros.' },
  { icon: '🤝', t: 'Sorted, end to end', d: 'Crew agreements and on-time payments keep both sides protected — no chasing.' },
];

const FEATURES = [
  { e: '🔔', t: 'Local job alerts', d: 'Get notified the instant a job is posted in your area.' },
  { e: '🎬', t: 'Find & post jobs', d: 'Browse gigs or post a role — photo and video, every field.' },
  { e: '✅', t: 'Quality-checked members', d: 'Every member is vetted with a valid ABN before they join.' },
  { e: '📄', t: 'Crew agreements', d: 'Every booking is backed by a clear agreement for both sides.' },
  { e: '💬', t: 'Built-in messaging', d: 'Sort the details with crew directly, right inside Shootzu.' },
  { e: '⚖️', t: 'Dispute resolution', d: 'If something goes sideways, we help both parties sort it out.' },
];

const REVIEWS = [
  {
    rName: 'Binh Nguyen', dName: 'James Anderson', rInit: 'BN', dInit: 'JA',
    rGrad: 'linear-gradient(135deg,#572B7C,#0C1120)', dGrad: 'linear-gradient(135deg,#78287C,#28C0D0)',
    quote: 'Great opportunity to work with James & Turbo360 — clear brief and fast payment. Thank you!',
    event: 'Growth Mixer · Networking Event', date: 'June 2026',
  },
  {
    rName: 'Mia Calder', dName: 'Dan Whitlock', rInit: 'MC', dInit: 'DW',
    rGrad: 'linear-gradient(135deg,#572B7C,#78287C)', dGrad: 'linear-gradient(135deg,#0C1120,#28C0D0)',
    quote: 'Dan slotted straight into the team and nailed the second-camera coverage. Total pro — booking again.',
    event: 'Wedding · Byron Bay, NSW', date: 'May 2026',
  },
  {
    rName: 'Priya Nair', dName: 'Sophie Tran', rInit: 'PN', dInit: 'ST',
    rGrad: 'linear-gradient(135deg,#67C6B4,#572B7C)', dGrad: 'linear-gradient(135deg,#28C0D0,#78287C)',
    quote: 'Booked Sophie for last-minute drone work — turned up early and the footage was unreal.',
    event: 'Brand Campaign · Gold Coast, QLD', date: 'April 2026',
  },
];

// Real peer reviews from /api/reviews/featured are normalised into the shape above.
const REVIEW_GRADS = [
  'linear-gradient(135deg,#572B7C,#0C1120)', 'linear-gradient(135deg,#78287C,#28C0D0)',
  'linear-gradient(135deg,#572B7C,#78287C)', 'linear-gradient(135deg,#0C1120,#28C0D0)',
  'linear-gradient(135deg,#67C6B4,#572B7C)', 'linear-gradient(135deg,#28C0D0,#78287C)',
];
const reviewInitials = (n) => (n || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const reviewDate = (s) => { try { return new Date(s).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }); } catch { return ''; } };
const normalizeReview = (r, i) => ({
  rName: r.reviewer_name || 'Crew', dName: r.reviewee_name || 'Crew',
  rInit: reviewInitials(r.reviewer_name), dInit: reviewInitials(r.reviewee_name),
  rPhoto: r.reviewer_photo || '', dPhoto: r.reviewee_photo || '',
  rGrad: REVIEW_GRADS[(i * 2) % REVIEW_GRADS.length], dGrad: REVIEW_GRADS[(i * 2 + 1) % REVIEW_GRADS.length],
  quote: r.review_text || '', event: r.job_title || 'Shootzu booking', date: reviewDate(r.created_at),
});

const FAQ = [
  { q: 'Who can join Shootzu?', a: 'Shootzu is for professional photographers, videographers, editors and assistants in Australia. Every member is quality-checked and needs a valid ABN, so you’re only ever working alongside vetted pros.' },
  { q: 'How fast will I hear about jobs?', a: 'The moment a job is posted in your area, it’s pinged straight to crew nearby — so you can be first to know and first to apply.' },
  { q: 'How does getting paid work?', a: 'Payments are handled through the platform and released on time once the job’s done. Every booking is backed by a crew agreement, so both sides know exactly where they stand.' },
  { q: 'Do I have to be a registered business?', a: 'You’ll need a valid ABN to join, but plenty of members are sole operators and freelancers. It keeps the network professional and the rates fair for everyone.' },
  { q: 'What kind of work is on Shootzu?', a: 'All stills and video fields — weddings, commercial, corporate, events, portraiture, editing and more. If it’s photo or video, the crew for it is here.' },
  { q: 'What if something goes wrong on a job?', a: 'We’ve got a dispute resolution process to help both parties reach a fair outcome, so you’re never left to sort it out alone.' },
];

const SYMBOL = '/assets/website/symbol-white.svg';
const APP_STORE_URL = 'https://apps.apple.com/au/app/shootzu/id6756085766';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=au.com.shootzu.app';

/* ------------------------- jobs-feed helpers ------------------------- */
const shortLocation = (addr) => {
  if (!addr) return '';
  const segs = String(addr).split(',').map((s) => s.trim()).filter(Boolean);
  return segs.slice(-2).join(', ');
};

const jobRoleTag = (job) => {
  if (Array.isArray(job.required_roles) && job.required_roles.length > 0) return job.required_roles[0];
  return job.job_type || 'Crew';
};

const jobDateLabel = (job) => {
  if (!job.date) return '';
  try {
    return format(new Date(job.date), 'EEE d MMM');
  } catch {
    return '';
  }
};

const jobPayLabel = (job) => {
  const crew = Array.isArray(job.crew_members) ? job.crew_members : [];
  const isAdvanced = crew.length > 1;
  if (isAdvanced) {
    const amount = job.total_amount || job.pay_rate || 0;
    return amount ? `Up to $${Number(amount).toLocaleString()}` : '';
  }
  if (job.total_amount) return `$${Number(job.total_amount).toLocaleString()}`;
  if (job.pay_rate) return `$${Number(job.pay_rate).toLocaleString()}/day`;
  return '';
};

/* =====================================================================
   Home — public marketing page (routes "/" and "/home")
   ===================================================================== */
const Home = () => {
  const [audience, setAudience] = useState('be'); // 'be' | 'find'
  const [openFaq, setOpenFaq] = useState(0);
  const [reviewIdx, setReviewIdx] = useState(0);

  const [jobs, setJobs] = useState([]);
  const [feedLabel, setFeedLabel] = useState('Across Australia');
  const [feedReady, setFeedReady] = useState(false);
  const [reviews, setReviews] = useState([]); // real reviews from API (normalized)

  /* ---- real peer reviews ---- */
  useEffect(() => {
    let alive = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/featured`)
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.reviews || []).map(normalizeReview).filter((r) => r.quote);
        if (list.length) { setReviews(list); setReviewIdx(0); }
      })
      .catch(() => {}); // fall back to bundled samples on failure
    return () => { alive = false; };
  }, []);

  /* ---- review carousel auto-rotate ---- */
  const rvList = reviews.length ? reviews : REVIEWS;
  useEffect(() => {
    const n = rvList.length;
    const id = setInterval(() => setReviewIdx((i) => (i + 1) % n), 6000);
    return () => clearInterval(id);
  }, [rvList.length]);

  /* ---- scroll to hash on mount (cross-page anchors) ---- */
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    }
  }, []);

  /* ---- real "Jobs near you" feed ---- */
  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;

    const fetchJobs = async (extra = '') => {
      const url = `${base}/api/jobs/available?user_email=guest@shootzu.com&page=1&limit=3${extra}`;
      const res = await axios.get(url);
      return res.data?.jobs || [];
    };

    const load = async () => {
      try {
        // First load with no geo so the feed always has something.
        const generic = await fetchJobs();
        if (!cancelled && generic.length > 0) {
          setJobs(generic.slice(0, 3));
          setFeedReady(true);
        }

        // Try to refine with the visitor's location for true "near you".
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              try {
                const { latitude, longitude } = pos.coords;
                const near = await fetchJobs(`&location_lat=${latitude}&location_lon=${longitude}&radius_km=200`);
                if (!cancelled && near.length > 0) {
                  setJobs(near.slice(0, 3));
                  setFeedLabel('Near you');
                  setFeedReady(true);
                }
              } catch {
                /* keep the generic feed */
              }
            },
            () => { /* permission denied — keep generic feed */ },
            { timeout: 8000 }
          );
        }
      } catch {
        if (!cancelled) {
          setJobs([]);
          setFeedReady(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const steps = audience === 'be' ? STEPS_BE : STEPS_FIND;
  const howTitle = audience === 'be' ? 'Find work in three taps' : 'Hire crew in three steps';
  const rv = rvList[reviewIdx % rvList.length] || rvList[0];
  const showFeed = feedReady && jobs.length > 0;

  /* ---------- shared style fragments ---------- */
  const SECTION = { maxWidth: 1180, margin: '0 auto', padding: '96px 40px 24px' };
  const EYEBROW_MAGENTA = { color: 'var(--wz-magenta)' };
  const H2 = { fontWeight: 700, fontSize: 'clamp(30px,4vw,46px)', letterSpacing: '-1px', margin: '14px 0 0' };

  return (
    <div className="wz-root" style={{ overflowX: 'hidden', background: '#fff', color: 'var(--wz-ink)' }}>
      {/* Responsive collapse for the design grids (scoped to this page). */}
      <style>{`
        @media (max-width: 900px) {
          .wz-root .hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; padding: 36px 24px !important; }
          .wz-root .hero-feed { display: none !important; }
          .wz-root .trust-row { gap: 22px !important; }
          .wz-root .split-grid { grid-template-columns: 1fr !important; }
          .wz-root .two-col { grid-template-columns: 1fr !important; }
          .wz-root .feat-grid { grid-template-columns: 1fr !important; }
          .wz-root .sec-pad { padding-left: 24px !important; padding-right: 24px !important; }
          .wz-root .app-card { padding: 38px 26px !important; }
        }
      `}</style>
      <PublicNav />

      {/* ============ HERO ============ */}
      <header
        id="top"
        style={{
          position: 'relative', background: 'var(--wz-ink)', overflow: 'hidden',
          minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* animated backdrop */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div className="wz-blob wz-drift1" style={{ width: 900, height: 720, top: -200, right: -180, background: 'radial-gradient(closest-side, rgba(120,40,124,.55), transparent 72%)' }} />
          <div className="wz-blob wz-drift2" style={{ width: 780, height: 640, bottom: -240, left: -200, background: 'radial-gradient(closest-side, rgba(40,192,208,.26), transparent 72%)' }} />
          <div className="wz-blob wz-drift3" style={{ width: 560, height: 480, top: '30%', left: '40%', background: 'radial-gradient(closest-side, rgba(87,43,124,.4), transparent 70%)' }} />
          <img className="wz-spin" src={SYMBOL} alt="" style={{ position: 'absolute', top: '46%', left: '64%', width: 620, height: 'auto', opacity: 0.05, transform: 'translate(-50%,-50%)' }} />
        </div>

        <div
          className="hero-grid"
          style={{
            position: 'relative', flex: 1, width: '100%', maxWidth: 1180, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 50, alignItems: 'center', padding: '56px 40px',
          }}
        >
          {/* LEFT */}
          <div>
            <div className="wz-eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 13, letterSpacing: '1.5px', color: 'var(--wz-aqua)', border: '1px solid rgba(40,192,208,.3)', borderRadius: 999, padding: '7px 15px' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--wz-aqua)', display: 'inline-block' }} />
              Be Crew &middot; live across Australia
            </div>
            <h1 className="wz-h1" style={{ fontWeight: 800, fontSize: 'clamp(40px,4.8vw,58px)', lineHeight: 1.02, letterSpacing: '-1.6px', color: '#fff', margin: '26px 0 0' }}>
              <span style={{ display: 'block' }}>The next shoot is</span>
              <span style={{ display: 'block' }}>looking for <span className="wz-grad-text">you.</span></span>
            </h1>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: 'rgba(255,255,255,.74)', maxWidth: '46ch', margin: '24px 0 0' }}>
              Shootzu pings local photo &amp; video jobs straight to you the moment they go live. A trusted network of vetted crew, real budgets and on-time pay &mdash; built by crew, for crew.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 34 }}>
              <Link to="/signup" className="wz-pill wz-pill-white" style={{ padding: '16px 30px', fontSize: 16 }}>Join the Zu Crew &rarr;</Link>
              <a href="#how" className="wz-pill wz-pill-outline-light" style={{ padding: '16px 30px', fontSize: 16 }}>See how it works</a>
            </div>
          </div>

          {/* RIGHT — real jobs feed */}
          {showFeed && (
            <div className="hero-feed" style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 24, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px 16px' }}>
                <span className="wz-h3" style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>Jobs near you</span>
                <span style={{ fontSize: 12, color: 'var(--wz-aqua)', fontWeight: 600 }}>{feedLabel}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {jobs.map((job, i) => {
                  const roleTag = jobRoleTag(job);
                  const loc = shortLocation(job.location_address);
                  const dateLabel = jobDateLabel(job);
                  const pay = jobPayLabel(job);
                  const meta = [loc, dateLabel].filter(Boolean).join(' · ');
                  return (
                    <Link
                      key={job.id || job._id || i}
                      to="/available-jobs"
                      style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', textDecoration: 'none', color: 'var(--wz-ink)', display: 'block' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div>
                          <div className="wz-h3" style={{ fontWeight: 600, fontSize: 15, color: 'var(--wz-ink)' }}>{job.title || roleTag}</div>
                          {meta && <div style={{ fontSize: 13, color: 'var(--wz-slate400)', marginTop: 3 }}>{meta}</div>}
                        </div>
                        {pay && (
                          <span className="wz-h3" style={{ fontWeight: 700, fontSize: 15, color: 'var(--wz-magenta)', whiteSpace: 'nowrap' }}>{pay}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 7, marginTop: 12, flexWrap: 'wrap' }}>
                        {roleTag && (
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--wz-violet)', background: 'var(--wz-slate100)', borderRadius: 999, padding: '5px 11px' }}>{roleTag}</span>
                        )}
                        {typeof job.distance_km === 'number' && (
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--wz-ink)', background: 'var(--wz-mint)', borderRadius: 999, padding: '5px 11px' }}>{Math.round(job.distance_km)}km away</span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* trust strip */}
        <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,.08)' }}>
          <div className="trust-row sec-pad" style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 30, padding: '26px 40px' }}>
            <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--wz-slate400)' }}>Trusted by crew across Australia</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 44 }}>
              {[['2,000+', 'Vetted professionals'], ['Every state', 'Coast to coast'], ['Photo + Video', 'One network']].map(([big, small]) => (
                <div key={small}>
                  <div className="wz-display" style={{ fontWeight: 800, fontSize: 28, color: '#fff' }}>{big}</div>
                  <div style={{ fontSize: 13, color: 'var(--wz-slate400)', marginTop: 2 }}>{small}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ============ WHY ============ */}
      <section className="sec-pad" style={SECTION}>
        <div className="wz-eyebrow" style={EYEBROW_MAGENTA}>WHY SHOOTZU</div>
        <h2 className="wz-h2" style={{ ...H2, maxWidth: '20ch' }}>Finding good crew shouldn&rsquo;t be a group chat at midnight.</h2>
        <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--wz-slate700)', maxWidth: '60ch', margin: '18px 0 0' }}>
          Shootzu replaces the scramble with one trusted network. Real professionals, real budgets, and the right people for the shoot &mdash; found in minutes, not days.
        </p>
        <div className="two-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginTop: 48 }}>
          {WHY_CARDS.map((c) => (
            <div key={c.t} style={{ border: '1px solid var(--wz-slate100)', borderRadius: 18, padding: 30 }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--wz-slate100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{c.icon}</div>
              <div className="wz-h3" style={{ fontWeight: 700, fontSize: 19, margin: '18px 0 8px' }}>{c.t}</div>
              <p style={{ margin: 0, color: 'var(--wz-slate700)', lineHeight: 1.6, fontSize: 15 }}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="sec-pad" style={SECTION}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24 }}>
          <div>
            <div className="wz-eyebrow" style={EYEBROW_MAGENTA}>HOW IT WORKS</div>
            <h2 className="wz-h2" style={H2}>{howTitle}</h2>
          </div>
          <div style={{ display: 'inline-flex', background: 'var(--wz-slate100)', borderRadius: 999, padding: 5, gap: 4 }}>
            {[['be', 'I want work'], ['find', 'I need crew']].map(([key, label]) => {
              const active = audience === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setAudience(key)}
                  style={{
                    fontFamily: "'Work Sans',sans-serif", fontSize: 14, fontWeight: 600, border: 'none',
                    borderRadius: 999, padding: '10px 20px', cursor: 'pointer', transition: 'background .15s,color .15s',
                    background: active ? 'var(--wz-ink)' : 'transparent', color: active ? '#fff' : 'var(--wz-slate700)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="two-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginTop: 44 }}>
          {steps.map((step) => (
            <div key={step.n} style={{ position: 'relative', border: '1px solid var(--wz-slate100)', borderRadius: 18, padding: '32px 30px', overflow: 'hidden' }}>
              <div className="wz-display" style={{ fontWeight: 800, fontSize: 54, lineHeight: 1, color: 'var(--wz-slate100)' }}>{step.n}</div>
              <div className="wz-h3" style={{ fontWeight: 700, fontSize: 20, margin: '14px 0 8px' }}>{step.t}</div>
              <p style={{ margin: 0, color: 'var(--wz-slate700)', lineHeight: 1.6, fontSize: 15 }}>{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ AUDIENCE SPLIT ============ */}
      <section id="audience" className="sec-pad" style={SECTION}>
        <div className="split-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* BE CREW */}
          <div style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', background: 'var(--wz-ink)', color: '#fff', display: 'flex', flexDirection: 'column' }}>
            <div style={{ width: '100%', height: 240, overflow: 'hidden' }}>
              <img src="/assets/website/be-crew-interview.jpg" alt="Crew on set" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 50%', display: 'block' }} />
            </div>
            <div style={{ padding: '34px 34px 38px' }}>
              <div className="wz-eyebrow" style={{ fontSize: 12, letterSpacing: '1.5px', color: 'var(--wz-aqua)' }}>Be Crew</div>
              <h3 className="wz-h3" style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-.6px', margin: '12px 0 10px' }}>Find more work, on your terms</h3>
              <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,.74)', lineHeight: 1.6, fontSize: 15 }}>Fill the quiet weeks, pick up second-shooter gigs and grow your reputation in a network that values your craft.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 26 }}>
                {['Local job alerts in real time', 'Apply in a tap, get booked fast', 'On-time payments, no chasing'].map((t) => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14.5, color: 'rgba(255,255,255,.9)' }}>
                    <span style={{ color: 'var(--wz-aqua)', fontWeight: 700 }}>&#10003;</span> {t}
                  </div>
                ))}
              </div>
              <Link to="/signup" className="wz-pill wz-pill-aqua">Join as crew &rarr;</Link>
            </div>
          </div>
          {/* FIND CREW */}
          <div style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', border: '1px solid var(--wz-slate100)', background: '#fff', display: 'flex', flexDirection: 'column' }}>
            <div style={{ width: '100%', height: 240, overflow: 'hidden' }}>
              <img src="/assets/website/find-crew-presenter.jpg" alt="Crew on a shoot" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 12%', display: 'block' }} />
            </div>
            <div style={{ padding: '34px 34px 38px' }}>
              <div className="wz-eyebrow" style={{ fontSize: 12, letterSpacing: '1.5px', color: 'var(--wz-magenta)' }}>Find Crew</div>
              <h3 className="wz-h3" style={{ fontWeight: 700, fontSize: 28, letterSpacing: '-.6px', margin: '12px 0 10px' }}>Build your crew in minutes</h3>
              <p style={{ margin: '0 0 20px', color: 'var(--wz-slate700)', lineHeight: 1.6, fontSize: 15 }}>Need a second shooter, an editor or a whole team? Post once and reach vetted pros in your area &mdash; fast.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 26 }}>
                {['Post a job free, set your own rate', 'Reach quality-checked crew nearby', 'Hire with a crew agreement in place'].map((t) => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14.5, color: 'var(--wz-slate700)' }}>
                    <span style={{ color: '#1F8A5B', fontWeight: 700 }}>&#10003;</span> {t}
                  </div>
                ))}
              </div>
              <Link to="/signup" className="wz-pill" style={{ background: 'var(--wz-magenta)', color: '#fff' }}>Post a job &rarr;</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="features" style={{ background: 'var(--wz-ink)', color: '#fff', marginTop: 96 }}>
        <div className="sec-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '90px 40px' }}>
          <div className="wz-eyebrow" style={{ color: 'var(--wz-aqua)' }}>FEATURES</div>
          <h2 className="wz-h2" style={{ ...H2, maxWidth: '18ch' }}>Everything to find work and manage it in one place.</h2>
          <div className="feat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 20, overflow: 'hidden', marginTop: 48 }}>
            {FEATURES.map((f) => (
              <div key={f.t} style={{ background: 'var(--wz-ink)', padding: '34px 30px' }}>
                <div style={{ fontSize: 26 }}>{f.e}</div>
                <div className="wz-h3" style={{ fontWeight: 700, fontSize: 18, margin: '16px 0 7px' }}>{f.t}</div>
                <p style={{ margin: 0, fontSize: 14.5, color: 'var(--wz-slate400)', lineHeight: 1.6 }}>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRUST / VETTING ============ */}
      <section className="sec-pad" style={SECTION}>
        <div className="split-grid" style={{ display: 'grid', gridTemplateColumns: '.95fr 1.05fr', gap: 48, alignItems: 'center' }}>
          <div style={{ width: '100%', height: 420, overflow: 'hidden', borderRadius: 22 }}>
            <img src="/assets/website/trust-location.jpg" alt="Professional on location" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 32%', display: 'block' }} />
          </div>
          <div>
            <div className="wz-eyebrow" style={EYEBROW_MAGENTA}>A NETWORK YOU CAN TRUST</div>
            <h2 className="wz-h2" style={{ fontWeight: 700, fontSize: 'clamp(28px,3.6vw,42px)', letterSpacing: '-.8px', margin: '14px 0 14px' }}>Professionals only. No exceptions.</h2>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: 'var(--wz-slate700)', maxWidth: '52ch', margin: '0 0 26px' }}>
              Shootzu is business-to-business and built to protect the industry &mdash; not undercut it. We won&rsquo;t let anyone advertise unpaid work or below-award rates, so the network stays fair for everyone on it.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 15, color: 'var(--wz-slate700)', lineHeight: 1.5 }}>
              {[
                ['Portfolio reviewed', ' — we check the work is up to standard and genuinely yours.'],
                ['ABN verified', ' — every member runs a real, active business.'],
                ['Fair rates only', ' — no “free”, no “for exposure”, no devaluing the craft.'],
              ].map(([bold, rest]) => (
                <div key={bold} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                  <span style={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', background: 'var(--wz-mint)', color: 'var(--wz-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>&#10003;</span>
                  <div><b style={{ fontFamily: "'Poppins',sans-serif" }}>{bold}</b>{rest}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PEER REVIEWS ============ */}
      <section className="sec-pad" style={SECTION}>
        <div className="wz-eyebrow" style={EYEBROW_MAGENTA}>THE ZU CREW</div>
        <h2 className="wz-h2" style={H2}>Crew who get it, get more work.</h2>
        <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--wz-slate700)', maxWidth: '54ch', margin: '16px 0 0' }}>
          Real reviews between crew, left after real jobs on Shootzu &mdash; both sides rate every booking.
        </p>
        <div style={{ position: 'relative', marginTop: 38, borderRadius: 28, overflow: 'hidden', background: 'var(--wz-grad-review)', border: '1px solid rgba(255,255,255,.08)', padding: '60px 40px 48px', textAlign: 'center' }}>
          <img src={SYMBOL} alt="" style={{ position: 'absolute', top: -34, right: -24, width: 200, opacity: 0.05 }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 30 }}>
            {/* reviewer */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 150 }}>
              <div style={{ position: 'relative', width: 84, height: 84 }}>
                <span style={{ width: 84, height: 84, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: 24, color: '#fff', boxShadow: '0 0 0 3px #7B5CFF', background: rv.rGrad }}>{rv.rInit}</span>
                {rv.rPhoto && (
                  <img src={rv.rPhoto} alt={rv.rName} referrerPolicy="no-referrer"
                    style={{ position: 'absolute', inset: 0, width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 3px #7B5CFF', zIndex: 1 }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <span style={{ position: 'absolute', left: '50%', bottom: -11, transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: '#fff', background: '#7B5CFF', borderRadius: 999, padding: '3px 12px', whiteSpace: 'nowrap', zIndex: 2 }}>Reviewer</span>
              </div>
              <div className="wz-h3" style={{ fontWeight: 600, fontSize: 15, color: '#fff', marginTop: 22 }}>{rv.rName}</div>
              <div style={{ fontSize: 13, color: 'var(--wz-slate400)', marginTop: 2 }}>Crew</div>
            </div>
            <span style={{ fontSize: 24, color: 'var(--wz-mint)', marginTop: 28 }}>&rarr;</span>
            {/* reviewed */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 150 }}>
              <div style={{ position: 'relative', width: 84, height: 84 }}>
                <span style={{ width: 84, height: 84, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: 24, color: '#fff', boxShadow: '0 0 0 3px #67C6B4', background: rv.dGrad }}>{rv.dInit}</span>
                {rv.dPhoto && (
                  <img src={rv.dPhoto} alt={rv.dName} referrerPolicy="no-referrer"
                    style={{ position: 'absolute', inset: 0, width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 3px #67C6B4', zIndex: 1 }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <span style={{ position: 'absolute', left: '50%', bottom: -11, transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: 'var(--wz-ink)', background: 'var(--wz-mint)', borderRadius: 999, padding: '3px 12px', whiteSpace: 'nowrap', zIndex: 2 }}>Reviewed</span>
              </div>
              <div className="wz-h3" style={{ fontWeight: 600, fontSize: 15, color: '#fff', marginTop: 22 }}>{rv.dName}</div>
              <div style={{ fontSize: 13, color: 'var(--wz-slate400)', marginTop: 2 }}>Crew</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 34, fontSize: 22, color: 'var(--wz-gold)', letterSpacing: '2px' }}>&#9733;&#9733;&#9733;&#9733;&#9733;</div>
          <p className="wz-display" style={{ fontWeight: 500, fontSize: 'clamp(20px,2.4vw,26px)', lineHeight: 1.4, letterSpacing: '-.4px', color: '#fff', maxWidth: '24ch', margin: '24px auto 0' }}>&ldquo;{rv.quote}&rdquo;</p>
          <div className="wz-h3" style={{ fontWeight: 600, fontSize: 15, color: 'var(--wz-aqua)', marginTop: 26 }}>{rv.event}</div>
          <div style={{ fontSize: 13.5, color: 'var(--wz-slate400)', marginTop: 6 }}>{rv.date}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 30 }}>
            {rvList.map((_, i) => {
              const active = i === reviewIdx;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Show review ${i + 1}`}
                  onClick={() => setReviewIdx(i)}
                  style={{
                    height: 9, border: 'none', cursor: 'pointer', padding: 0, transition: 'background .2s, width .2s',
                    width: active ? 26 : 9, borderRadius: active ? 999 : '50%',
                    background: active ? 'var(--wz-aqua)' : 'rgba(255,255,255,.25)',
                  }}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ APP DOWNLOAD ============ */}
      <section className="sec-pad" style={SECTION}>
        <div className="split-grid app-card" style={{ display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 40, alignItems: 'center', background: 'var(--wz-grad-app)', borderRadius: 28, padding: '56px 50px', color: '#fff', overflow: 'hidden' }}>
          <div>
            <div className="wz-eyebrow" style={{ color: 'var(--wz-aqua)' }}>ON THE GO</div>
            <h2 className="wz-h2" style={{ fontWeight: 700, fontSize: 'clamp(28px,3.6vw,42px)', letterSpacing: '-.8px', margin: '14px 0 14px' }}>Take the network in your pocket.</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,.8)', maxWidth: '46ch', margin: '0 0 28px' }}>
              Get job alerts, apply and message crew the moment you&rsquo;re needed &mdash; wherever the shoot takes you.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store" style={{ display: 'inline-block', lineHeight: 0 }}>
                <img src="/assets/app-store-badge.svg" alt="Download on the App Store" style={{ height: 54, display: 'block' }} />
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play" style={{ display: 'inline-block', lineHeight: 0 }}>
                <img src="/assets/google-play-badge.svg" alt="Get it on Google Play" style={{ height: 54, display: 'block' }} />
              </a>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <img src={SYMBOL} alt="" style={{ position: 'absolute', width: 340, opacity: 0.08, top: -40 }} />
            <div style={{ position: 'relative', width: 240, background: 'var(--wz-ink)', borderRadius: 38, padding: 11, boxShadow: '0 30px 60px rgba(0,0,0,.35)' }}>
              <div style={{ background: '#15192b', borderRadius: 28, overflow: 'hidden' }}>
                <div style={{ padding: '20px 20px 22px' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--wz-aqua)', fontWeight: 600, letterSpacing: '1px' }}>NEW JOB &middot; 2KM AWAY</div>
                  <div className="wz-h3" style={{ fontWeight: 600, fontSize: 17, color: '#fff', marginTop: 7 }}>2nd Shooter wanted</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 3 }}>Wedding &middot; Sat 14 Jun &middot; Byron Bay</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,.06)', borderRadius: 12, padding: '12px 14px', marginTop: 16 }}>
                    <span style={{ fontSize: 12, color: 'var(--wz-slate400)' }}>Day rate</span>
                    <span className="wz-h3" style={{ fontWeight: 700, fontSize: 17, color: '#fff' }}>$650</span>
                  </div>
                  <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 13.5, color: 'var(--wz-ink)', background: 'var(--wz-aqua)', borderRadius: 11, padding: 12, marginTop: 12 }}>Apply in one tap</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq" className="sec-pad" style={{ maxWidth: 860, margin: '0 auto', padding: '96px 40px 24px' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="wz-eyebrow" style={EYEBROW_MAGENTA}>FAQ</div>
          <h2 className="wz-h2" style={{ fontWeight: 700, fontSize: 'clamp(30px,4vw,46px)', letterSpacing: '-1px', margin: '14px 0 44px' }}>Good questions, straight answers.</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FAQ.map((item, i) => {
            const open = openFaq === i;
            return (
              <div key={item.q} style={{ border: '1px solid var(--wz-slate100)', borderRadius: 16, overflow: 'hidden' }}>
                <div
                  onClick={() => setOpenFaq(open ? null : i)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '22px 24px', cursor: 'pointer' }}
                >
                  <span className="wz-h3" style={{ fontWeight: 600, fontSize: 17 }}>{item.q}</span>
                  <span style={{ flex: 'none', fontFamily: "'Poppins',sans-serif", fontWeight: 600, fontSize: 22, color: 'var(--wz-magenta)', lineHeight: 1 }}>{open ? '−' : '+'}</span>
                </div>
                {open && (
                  <p style={{ margin: 0, padding: '0 24px 24px', color: 'var(--wz-slate700)', lineHeight: 1.65, fontSize: 15.5, maxWidth: '64ch' }}>{item.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section id="join" className="sec-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '96px 40px 110px' }}>
        <div style={{ position: 'relative', background: 'var(--wz-ink)', borderRadius: 28, padding: '80px 40px', textAlign: 'center', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(680px 420px at 50% -10%, rgba(120,40,124,.6), transparent 62%),radial-gradient(560px 380px at 80% 120%, rgba(40,192,208,.28), transparent 60%)' }} />
          <div style={{ position: 'relative' }}>
            <img src={SYMBOL} alt="" style={{ width: 58, margin: '0 auto 22px', display: 'block' }} />
            <h2 className="wz-display" style={{ fontWeight: 800, fontSize: 'clamp(34px,5vw,58px)', letterSpacing: '-1.4px', color: '#fff', margin: '0 auto', maxWidth: '16ch', lineHeight: 1 }}>Your next gig is one tap away.</h2>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: 'rgba(255,255,255,.75)', maxWidth: '48ch', margin: '22px auto 34px' }}>
              Join the Zu Crew free. Set up your profile, get quality-checked and start seeing local jobs today.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
              <Link to="/signup" className="wz-pill wz-pill-aqua" style={{ padding: '17px 34px', fontSize: 16 }}>Join the Zu Crew &rarr;</Link>
              <Link to="/signup" className="wz-pill wz-pill-outline-light" style={{ padding: '17px 34px', fontSize: 16 }}>Post a job</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
